import React, { useState, useRef, useEffect } from 'react';
import '../styles/chat-widget.css';
import { FaStar, FaCommentDots, FaPaperPlane, FaUserMd, FaCalendarCheck, FaUserPlus } from 'react-icons/fa';
import { FaMoneyBillWave } from 'react-icons/fa';
import LoginModal from './LoginModal';
import { useChatWidget } from '../context/ChatWidgetContext';

function ChatWidget() {
    const { isLarge, isOpen, closeChat } = useChatWidget();
    // Random assistant names
    const assistantNames = ['Anna', 'Sarah', 'Emma', 'Sophie', 'Mia', 'Lily', 'Grace', 'Chloe', 'Zoe', 'Ava'];
    
    // Get random assistant name
    const getRandomAssistantName = () => {
        return assistantNames[Math.floor(Math.random() * assistantNames.length)];
    };

    const [assistantName] = useState(getRandomAssistantName());
    const [messages, setMessages] = useState([
        { text: `Hi, I'm ${assistantName}, your virtual assistant. Tell me your symptoms, and I'll connect you with the right practitioner.`, sender: 'bot' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const [open, setOpen] = useState(false); // Local state for normal chat widget
    const [bookingLoading, setBookingLoading] = useState(null); // doctor email for which booking is loading
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingDoctorEmail, setPendingDoctorEmail] = useState(null);
    const [cookiesHandled, setCookiesHandled] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const sliderRef = useRef(null);
    const autoScrollIntervalRef = useRef(null);
    const [showCarouselNav, setShowCarouselNav] = useState(false);
    const carouselNavTimeoutRef = useRef(null);

    // Helper to show nav and auto-hide after delay
    const showNavWithTimeout = () => {
        setShowCarouselNav(true);
        if (carouselNavTimeoutRef.current) clearTimeout(carouselNavTimeoutRef.current);
        carouselNavTimeoutRef.current = setTimeout(() => setShowCarouselNav(false), 2000);
    };

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (carouselNavTimeoutRef.current) clearTimeout(carouselNavTimeoutRef.current);
        };
    }, []);

    const checkCookiesAndShowChat = () => {
        const cookieConsent = localStorage.getItem('cookieConsent');
        if (cookieConsent) {
            setCookiesHandled(true);
            // Small delay to ensure smooth transition
            setTimeout(() => {
                setOpen(true);
            }, 500);
        }
        setInitialized(true);
    };

    // Check if cookies have been handled with a small delay to prevent glitch
    useEffect(() => {
        // Add a small delay to prevent the glitch on page reload
        const timer = setTimeout(checkCookiesAndShowChat, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // Listen for localStorage changes (when cookies are accepted)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'cookieConsent' && e.newValue) {
                // Cookies were just set, show the chat widget
                setTimeout(() => {
                    setCookiesHandled(true);
                    setOpen(true);
                }, 300);
            }
        };

        // Listen for storage events (works across tabs)
        window.addEventListener('storage', handleStorageChange);

        // Also listen for custom events (for same-tab updates)
        const handleCustomEvent = () => {
            setTimeout(() => {
                setCookiesHandled(true);
        setOpen(true);
            }, 300);
        };
        
        window.addEventListener('cookiesAccepted', handleCustomEvent);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('cookiesAccepted', handleCustomEvent);
        };
    }, []);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Auto-scroll functionality for doctor carousel
    useEffect(() => {
        const currentMessage = messages.find(msg => msg.type === 'doctors');
        if (currentMessage && currentMessage.doctors && currentMessage.doctors.length > 1 && !isPaused) {
            autoScrollIntervalRef.current = setInterval(() => {
                if (!isDragging) {
                    setCurrentSlide(prev => {
                        const totalSlides = Math.ceil(currentMessage.doctors.length / 1);
                        const nextSlide = (prev + 1) % totalSlides;
                        return nextSlide;
                    });
                }
            }, 4000); // Change slide every 4 seconds
        }

        return () => {
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
            }
        };
    }, [messages, isDragging, isPaused]);

    // Update scroll position when currentSlide changes
    useEffect(() => {
        if (sliderRef.current && !isDragging) {
            const cardWidth = 320 + 20; // card width + gap
            const scrollPosition = currentSlide * cardWidth;
            sliderRef.current.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
            });
        }
    }, [currentSlide, isDragging]);

    // Listen for scroll events to update current slide
    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider) return;

        const handleScroll = () => {
            if (!isDragging) {
                const cardWidth = 320 + 20; // card width + gap
                const currentScroll = slider.scrollLeft;
                const newSlide = Math.round(currentScroll / cardWidth);
                setCurrentSlide(Math.max(0, newSlide));
            }
        };

        slider.addEventListener('scroll', handleScroll);
        return () => slider.removeEventListener('scroll', handleScroll);
    }, [isDragging]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const sendMessage = async () => {
        if (!input.trim()) return;
        const userMessage = { text: input, sender: 'user' };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        
        // Keep focus on input field
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
        
        try {
            const response = await fetch('https://chat.virtualtriage.ca/chat/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    query: input,
                    session_id: sessionStorage.getItem('session_id') || (() => {
                        const newSessionId = Date.now().toString();
                        sessionStorage.setItem('session_id', newSessionId);
                        return newSessionId;
                    })()
                })
            });
            const data = await response.json();
            const newMessages = [{ text: data.message || 'No response from server.', sender: 'bot' }];
            
            // Add suggested responses if available
            if (data.suggested_responses && data.suggested_responses.length > 0) {
                newMessages.push({
                    type: 'suggested_responses',
                    responses: data.suggested_responses
                });
            }
            
            if (Array.isArray(data.doctors) && data.doctors.length > 0) {
                    newMessages.push({
                    type: 'doctors',
                    doctors: data.doctors.map(doctor => ({
                        Full_Name: doctor.Full_Name,
                        Specialization: doctor.doctor_speciality,
                        Rating: doctor.doctor_rating,
                        Image: doctor.doctor_image_url,
                        Fee: doctor.doctor_fees,
                        Email: doctor.doctor_email
                    }))
                });
            }
            setMessages(prev => [...prev, ...newMessages]);
        } catch (error) {
            setMessages(prev => [...prev, { text: 'Sorry, there was an error connecting to the chat server.', sender: 'bot' }]);
        } finally {
            setLoading(false);
            // Keep focus on input field after response
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 0);
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default to avoid form submission
            sendMessage();
        }
    };

    // Mouse/Touch event handlers for carousel dragging
    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setStartX(e.pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        const newScrollLeft = scrollLeft - walk;
        sliderRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, sliderRef.current.scrollWidth - sliderRef.current.clientWidth));
    };

    // Touch event handlers for mobile
    const handleTouchStart = (e) => {
        setIsDragging(true);
        setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        const newScrollLeft = scrollLeft - walk;
        sliderRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, sliderRef.current.scrollWidth - sliderRef.current.clientWidth));
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    // Navigation functions for carousel
    const goToSlide = (slideIndex) => {
        setCurrentSlide(slideIndex);
    };

    const nextSlide = () => {
        const currentMessage = messages.find(msg => msg.type === 'doctors');
        if (currentMessage && currentMessage.doctors) {
            const totalSlides = currentMessage.doctors.length;
            setCurrentSlide(prev => (prev + 1) % totalSlides);
        }
    };

    const prevSlide = () => {
        const currentMessage = messages.find(msg => msg.type === 'doctors');
        if (currentMessage && currentMessage.doctors) {
            const totalSlides = currentMessage.doctors.length;
            setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
        }
    };

    // Handle suggested response click
    const handleSuggestedResponse = (response) => {
        // Add the suggested response as a user message immediately
        const userMessage = { text: response, sender: 'user' };
        setMessages(prev => [...prev, userMessage]);
        
        // Send the message to the API
        sendSuggestedMessage(response);
    };

    // Send suggested message to API
    const sendSuggestedMessage = async (messageText) => {
        setLoading(true);
        
        try {
            const response = await fetch('https://chat.virtualtriage.ca/chat/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    query: messageText,
                    session_id: sessionStorage.getItem('session_id') || (() => {
                        const newSessionId = Date.now().toString();
                        sessionStorage.setItem('session_id', newSessionId);
                        return newSessionId;
                    })()
                })
            });
            const data = await response.json();
            const newMessages = [{ text: data.message || 'No response from server.', sender: 'bot' }];
            
            // Add suggested responses if available
            if (data.suggested_responses && data.suggested_responses.length > 0) {
                newMessages.push({
                    type: 'suggested_responses',
                    responses: data.suggested_responses
                });
            }
            
            if (Array.isArray(data.doctors) && data.doctors.length > 0) {
                newMessages.push({
                    type: 'doctors',
                    doctors: data.doctors.map(doctor => ({
                        Full_Name: doctor.Full_Name,
                        Specialization: doctor.doctor_speciality,
                        Rating: doctor.doctor_rating,
                        Image: doctor.doctor_image_url,
                        Fee: doctor.doctor_fees,
                        Email: doctor.doctor_email
                    }))
                });
            }
            setMessages(prev => [...prev, ...newMessages]);
        } catch (error) {
            setMessages(prev => [...prev, { text: 'Sorry, there was an error connecting to the chat server.', sender: 'bot' }]);
        } finally {
            setLoading(false);
            // Keep focus on input field after response
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 0);
        }
    };

    const handleBookAppointment = async (doctorEmail) => {
        console.log("handleBookAppointment called with doctorEmail:", doctorEmail);
        
        // Check if user is logged in
        const authToken = localStorage.getItem("authToken");
        
        if (!authToken) {
            console.log("User not logged in, storing doctor email and opening login modal");
            // User is not logged in, store doctor email and open login modal
            setPendingDoctorEmail(doctorEmail);
            setShowLoginModal(true);
            return;
        }
        
        // User is logged in, proceed with booking
        setBookingLoading(doctorEmail);
        console.log("User is logged in, proceeding with booking");
        
        try {
            const response = await fetch("https://portal.virtualtriage.ca/api/v1/patient/appointment/book-appointment/directory/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    doctor_email: doctorEmail
                }),
            });
            
            const data = await response.json();
            console.log("Booking API response:", data);
            
            if (data.status) {
                alert("Appointment booked successfully!");
            } else if (data.status === false && data.clinic_id) {
                // Redirect to the website's clinic details page
                    window.location.href = `https://patient.virtualtriage.ai/clinic-details/${data.clinic_id}`;
            } else {
                alert(data.message || "Failed to book appointment.");
            }
        } catch (error) {
            console.error("Error booking appointment:", error);
            alert("Error booking appointment. Please try again later.");
        } finally {
            setBookingLoading(null);
        }
    };

    const handleLoginSuccess = async (doctorEmail) => {
        console.log("handleLoginSuccess called with doctorEmail:", doctorEmail);
        
        try {
            const authToken = localStorage.getItem("authToken");
            const userData = JSON.parse(localStorage.getItem("userData") || "{}");

            if (!authToken) {
                console.error("No auth token found after login");
                return;
            }

            // Make the booking API call
            const response = await fetch("https://portal.virtualtriage.ca/api/v1/patient/appointment/book-appointment/directory/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    doctor_email: doctorEmail
                }),
            });

            const data = await response.json();
            console.log("API Response after login:", data);

            if (data.status === false && data.clinic_id) {
                // Redirect to the website's clinic details page
                window.location.href = `https://patient.virtualtriage.ai/clinic-details/${data.clinic_id}`;
            } else {
                alert(data.message || "Failed to book appointment.");
            }
        } catch (error) {
            console.error("Error booking appointment after login:", error);
            alert("Error booking appointment. Please try again later.");
        }
    };

    // Handle chat close with API call
    const handleCloseChatLocal = async () => {
        try {
            const sessionId = sessionStorage.getItem('session_id');
            if (sessionId) {
                await fetch('https://chat.virtualtriage.ca/chat/close', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sessionId })
                });
            }
        } catch (error) {
            console.error('Error closing chat session:', error);
        } finally {
            // Clear the chat messages and close the widget
            setMessages([
                { text: `Hi, I'm ${assistantName}, your virtual assistant. If you have any symptoms, let me know so I can connect you with the right practitioner.`, sender: 'bot' }
            ]);
            // Use context closeChat if in large mode, otherwise use local state
            if (isLarge) {
                closeChat();
            } else {
                setOpen(false);
            }
        }
    };

    // Don't render if cookies haven't been handled yet or component hasn't initialized
    if (!cookiesHandled || !initialized) {
        return null;
    }

    // Determine if chat should be open (either from context or local state)
    const shouldShowChat = isLarge ? isOpen : open;
    
    // Handle mini widget click
    const handleMiniWidgetClick = () => {
        if (isLarge) {
            // If we're in large mode, close it and open small
            closeChat();
            setOpen(true);
        } else {
            // Normal small widget behavior
            setOpen(true);
        }
    };

    // Show mini widget when closed
    if (!shouldShowChat) {
        console.log('Rendering mini widget, open state:', shouldShowChat);
        return (
            <div 
                className="chat-widget-mini" 
                onClick={handleMiniWidgetClick}
                style={{
                    position: 'fixed',
                    bottom: '32px',
                    right: '32px',
                    width: '120px',
                    height: '50px',
                    borderRadius: '25px',
                    background: 'linear-gradient(135deg, #50cada 0%, #3bb8c8 100%)',
                    boxShadow: '0 12px 40px rgba(80, 202, 218, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    zIndex: 3000,
                    color: 'white',
                    fontSize: '20px',
                    gap: '8px',
                    padding: '0 16px',
                    transition: 'all 0.3s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: '600'
                }}
            >
                <FaCommentDots style={{ fontSize: '24px' }} />
                <span>Chat</span>
            </div>
        );
    }

    return (
        <>
            {isLarge && (
                <div 
                    className="chat-widget-backdrop"
                    onClick={handleCloseChatLocal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 3999,
                        backdropFilter: 'blur(4px)'
                    }}
                />
            )}
            <div className={`chat-widget ${isLarge ? 'chat-widget-large' : ''}`} style={isLarge ? {
                width: '80vw',
                height: '80vh',
                maxWidth: '1200px',
                maxHeight: '800px',
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 4000,
                borderRadius: '20px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            } : {}}>
                {/* Header */}
                <div className="chat-widget-header">
                    <div className="chat-widget-header-logo">
                        <img 
                            src="/assets/Virtual Triage.png" 
                            alt="Virtual Triage Logo" 
                        />
                    </div>
                    <div className="chat-widget-header-text">
                        <h3>Virtual Assistant</h3>
                        <div className="chat-widget-online-status">
                            <div className="online-dot"></div>
                            <span>24/7 Online</span>
                        </div>
                    </div>
                    <div className="chat-widget-header-actions">
                        {!isLarge && (
                            <button 
                                className="header-minimize-btn"
                                onClick={() => {
                                    console.log('Minimize button clicked, setting open to false');
                                    setOpen(false);
                                }} 
                                title="Minimize"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>
                        )}
                        <button 
                            className="header-close-btn"
                            onClick={handleCloseChatLocal} 
                            title="Close"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div className="chat-widget-messages">
                    {messages.map((msg, idx) => (
                        <div key={`msg-${idx}`} className="message-container">
                            {msg.type === 'doctors' ? (
                                <div className="chat-widget-doctor-carousel"
                                    onMouseEnter={showNavWithTimeout}
                                    onMouseMove={showNavWithTimeout}
                                    onFocus={showNavWithTimeout}
                                    onMouseLeave={() => setShowCarouselNav(false)}
                                    tabIndex={0}
                                >
                                    {showCarouselNav && msg.doctors.length > 1 && (
                                        <>
                                            <button
                                                className="chat-widget-carousel-nav chat-widget-carousel-nav-left"
                                                onClick={prevSlide}
                                                aria-label="Previous doctor"
                                            >
                                                &#8592;
                                            </button>
                                            <button
                                                className="chat-widget-carousel-nav chat-widget-carousel-nav-right"
                                                onClick={nextSlide}
                                                aria-label="Next doctor"
                                            >
                                                &#8594;
                                            </button>
                                        </>
                                    )}
                                    <div 
                                        className="chat-widget-doctor-slider"
                                        ref={sliderRef}
                                        onMouseEnter={() => setIsPaused(true)}
                                        onMouseDown={handleMouseDown}
                                        onMouseUp={handleMouseUp}
                                        onMouseMove={handleMouseMove}
                                        onMouseLeave={handleMouseLeave}
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                    >
                                        {msg.doctors.map((doctor, doctorIdx) => (
                                            <div key={`doctor-${doctorIdx}`} className="chat-widget-doctor-card">
                                                <div className="chat-widget-doctor-card-row">
                                                    <img
                                                        src={doctor.Image || process.env.PUBLIC_URL + "/assets/Logo.jpg"}
                                                    alt={doctor.Full_Name}
                                        className="chat-widget-doctor-img"
                                                        onError={e => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + "/assets/Logo.jpg"; }}
                                                    />
                                                    <div className="chat-widget-doctor-info">
                                                        <div className="chat-widget-doctor-name">{doctor.Full_Name}</div>
                                                        <div className="chat-widget-doctor-specialty">{doctor.Specialization || 'General Practitioner'}</div>
                                                        <div className="chat-widget-doctor-rating">
                                                            Ratings: <span className="stars">
                                                                {[1, 2, 3, 4, 5].map((i) => (i <= Math.round(doctor.Rating || 0) ? "★" : "☆"))}
                                                            </span>
                                                        </div>
                                            </div>
                                                </div>
                                                <div className="chat-widget-doctor-actions">
                                                    <button className="chat-widget-doctor-btn">
                                                        <FaUserMd className="icon-margin-right"/>View Profile
                                                    </button>
                                                    {doctor.status === true ? (
                                                        <button 
                                                            className="chat-widget-doctor-btn book-appointment-btn"
                                                            onClick={() => handleBookAppointment(doctor.Email)}
                                                            disabled={bookingLoading === doctor.Email}
                                                        >
                                                            <FaCalendarCheck className="icon-margin-right"/>
                                                            {bookingLoading === doctor.Email ? 'Booking...' : 'Book Appointment'}
                                                        </button>
                                                    ) : doctor.status === false ? (
                                                <button
                                                            className="chat-widget-doctor-btn book-appointment-btn"
                                                    onClick={() => handleBookAppointment(doctor.Email)}
                                                    disabled={bookingLoading === doctor.Email}
                                                >
                                                            <FaCalendarCheck className="icon-margin-right"/>
                                                            {bookingLoading === doctor.Email ? 'Booking...' : 'Book Appointment'}
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="chat-widget-doctor-btn invite-btn"
                                                            onClick={() => {
                                                                // Handle invite functionality
                                                                console.log('Invite doctor:', doctor.Email);
                                                            }}
                                                        >
                                                            <FaUserPlus className="icon-margin-right" />Invite
                                                        </button>
                                                    )}
                                                </div>
                                                {/* Badges */}
                                                {doctor.status === true && (
                                                    <div className="instant-book-badge">
                                                        ⚡ Instant Book
                                                    </div>
                                                )}
                                                {doctor.status === false && (
                                                    <div className="approval-required-badge">
                                                        ⏳ Approval Required
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {msg.doctors.length > 1 && (
                                        <div className="chat-widget-carousel-dots">
                                            {msg.doctors.map((_, index) => (
                                                <button
                                                    key={index}
                                                    className={`chat-widget-carousel-dot ${currentSlide === index ? 'active' : ''}`}
                                                    onClick={() => goToSlide(index)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : msg.type === 'suggested_responses' ? (
                                <div className="suggested-responses">
                                    {msg.responses.map((response, idx) => (
                                        <button
                                            key={`response-${idx}`}
                                            className="suggested-response-btn"
                                            onClick={() => handleSuggestedResponse(response)}
                                        >
                                            {response}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className={`chat-widget-message${msg.sender === 'user' ? ' user' : ' bot'}`}>
                                    {msg.sender === 'bot' && (
                                        <div className="bot-avatar">
                                            <img 
                                                src="/assets/Virtual Triage.png" 
                                                alt="Virtual Triage" 
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="message-content">
                                    <p>{msg.text}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="chat-widget-input">
                    <div className="input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Type your message here..."
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        disabled={loading}
                    />
                    <button 
                        onClick={sendMessage} 
                        disabled={loading || !input.trim()}
                        className="send-button"
                        title="Send message"
                    >
                            {loading ? (
                                <span className="loading-spinner"></span>
                            ) : (
                                <FaPaperPlane />
                            )}
                    </button>
                    </div>
                </div>
            </div>
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onLoginSuccess={() => {
                    if (pendingDoctorEmail) {
                        handleLoginSuccess(pendingDoctorEmail);
                        setPendingDoctorEmail(null);
                    }
                }}
            />
        </>
    );
}

export default ChatWidget;